/* ============================================================
   BNE STUDIO — Vision (Midnight Blueprint)
   Recreated from the design handoff "BNE Studio - Vision.html".
   Renders inside NightLayout; page-specific styles live in
   night-vision.css. Storyboard frames + reel use ImageSlot.
   Route: /vision.
   ============================================================ */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import NightLayout from "../components/night/NightLayout";
import ImageSlot from "../components/night/ImageSlot";
import "../styles/night-vision.css";

const Arrow = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M5 12h14m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Check = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ANALYZER = [40, 70, 30, 85, 50, 95, 35, 65, 80, 28, 55, 90, 45, 72, 33, 88, 52, 68, 30, 82, 48, 76, 60, 92];
const INPUT_WAVE = [9, 20, 13, 27, 11, 22, 16, 28, 12, 24, 9, 18, 14, 25];
const VISION_URL = "https://vision.bne.studio";

const VisionPage = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "BNE Vision — Turn a song into a visual rollout";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <NightLayout description="Upload a track, read its sections and energy, then plan a full music video or make a post-ready reel — and track its reach." active="vision" variant="pg-vision">
      <div className="vision-wash" />

      {/* HERO */}
      <header className="phero" style={{ "--c": "var(--p-vision)" }}>
        <div className="wrap">
          <div className="phero-grid">
            <div className="reveal">
              <div className="ico-badge"><svg viewBox="0 0 24 24" fill="none"><path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3ZM3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
              <span className="eyebrow" style={{ marginBottom: "16px" }}>Create · Vision</span>
              <h1 className="grad-text">One song. Endless video.</h1>
              <p className="lead">Drop in a track and turn it into a full music video — or a scroll-stopping reel — in minutes.</p>
              <div className="ctas">
                <a className="btn btn-primary btn-lg btn-arrow" href={VISION_URL}>Create a Visual <Arrow /></a>
                <span className="domain">vision.bne.studio</span>
              </div>
            </div>
            <div className="reveal" data-d="1">
              <div className="big-mock">
                <div className="bm-bar"><i /><i /><i /><span className="t">vision.bne.studio / neon-hours</span><span className="pill">Storyboard</span></div>
                <div className="bm-body">
                  <div className="analyzer">
                    {ANALYZER.map((h, i) => <i key={i} style={{ height: h + "%" }} />)}
                  </div>
                  <div className="sections"><span>Intro</span><span className="on">Verse</span><span>Chorus</span><span>Bridge</span><span>Outro</span></div>
                  <div className="sb">
                    <ImageSlot className="f" id="vsb-1" shape="rounded" radius={8} placeholder="01 · Wide" />
                    <ImageSlot className="f" id="vsb-2" shape="rounded" radius={8} placeholder="02 · Close" />
                    <ImageSlot className="f" id="vsb-3" shape="rounded" radius={8} placeholder="03 · Drive" />
                    <ImageSlot className="f" id="vsb-4" shape="rounded" radius={8} placeholder="04 · Crowd" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* WORKFLOW */}
      <section className="section tight" style={{ "--c": "var(--p-vision)" }}>
        <div className="wrap">
          <div className="sec-head reveal"><span className="eyebrow">How it works</span><h2>Upload a song. BNE reads the rest.</h2></div>
          <div className="flow reveal">
            <div className="fstage">
              <div className="fk">Input</div>
              <div className="fv"><span className="wave" style={{ "--wh": "34px", color: "var(--p-vision)" }}>{INPUT_WAVE.map((h, i) => <i key={i} style={{ height: h + "px" }} />)}</span></div>
              <div className="ft">Your track</div><div className="fp">Finished or rough — drop it in.</div>
            </div>
            <div className="farr"><Arrow /></div>
            <div className="fstage">
              <div className="fk">Read</div>
              <div className="fv" style={{ gap: "4px" }}><span style={{ flex: 1, height: "24px", borderRadius: "4px", background: "linear-gradient(180deg,#6f86ff,#2f48ff)", opacity: .5 }} /><span style={{ flex: 1, height: "24px", borderRadius: "4px", background: "linear-gradient(180deg,#a877ff,#6f86ff)", opacity: .8 }} /><span style={{ flex: 1, height: "24px", borderRadius: "4px", background: "linear-gradient(180deg,#a877ff,#c23ad6)" }} /><span style={{ flex: 1, height: "24px", borderRadius: "4px", background: "linear-gradient(180deg,#6f86ff,#2f48ff)", opacity: .5 }} /></div>
              <div className="ft">Tempo &amp; sections</div><div className="fp">Intro, verse, chorus, hook.</div>
            </div>
            <div className="farr"><Arrow /></div>
            <div className="fstage">
              <div className="fk">Build</div>
              <div className="fv" style={{ gap: "4px" }}><span style={{ flex: 1, height: "42px", borderRadius: "5px", background: "linear-gradient(140deg,#241a40,#a877ff)" }} /><span style={{ flex: 1, height: "42px", borderRadius: "5px", background: "linear-gradient(140deg,#3a1233,#c23ad6)" }} /><span style={{ flex: 1, height: "42px", borderRadius: "5px", background: "linear-gradient(140deg,#10193e,#2f48ff)" }} /></div>
              <div className="ft">Storyboard</div><div className="fp">Scenes mapped to the song.</div>
            </div>
            <div className="farr"><Arrow /></div>
            <div className="fstage">
              <div className="fk">Ship</div>
              <div className="fv"><span style={{ height: "48px", aspectRatio: "9/16", borderRadius: "6px", background: "linear-gradient(165deg,#2a1840,#0c0e15)", display: "grid", placeItems: "center" }}><svg viewBox="0 0 24 24" width="11" height="11" fill="#fff"><path d="M8 5v14l11-7z" /></svg></span></div>
              <div className="ft">Video or reel</div><div className="fp">Post-ready, then track reach.</div>
            </div>
          </div>
        </div>
      </section>

      {/* TWO MODES */}
      <section className="section" style={{ "--c": "var(--p-vision)" }}>
        <div className="wrap">
          <div className="sec-head reveal"><span className="eyebrow">Two ways to work</span><h2>Plan the video, or ship a reel.</h2><p>One platform, two creative paths — the room where you imagine the whole film, and the desk where you ship something today.</p></div>
          <div className="mode-grid">
            <div className="mode reveal" data-d="1">
              <div className="mi"><svg viewBox="0 0 24 24" fill="none"><path d="M4 4h16v16H4zM4 9h16M9 4v5M4 14h16M9 14v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
              <h3>Plan the Video</h3>
              <p>The creative room. Vision drafts a director's treatment from the song and lets you shape the whole film before a frame is shot.</p>
              <ul>
                <li><Check />Logline, treatment &amp; mood palette</li>
                <li><Check />Scene-by-scene storyboard tied to sections</li>
                <li><Check />Shot list &amp; reference frames</li>
              </ul>
            </div>
            <div className="mode reveal" data-d="2">
              <div className="mi"><svg viewBox="0 0 24 24" fill="none"><path d="M7 2h10a1 1 0 011 1v18a1 1 0 01-1 1H7a1 1 0 01-1-1V3a1 1 0 011-1zM10 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
              <h3>Make a Reel</h3>
              <p>The shipping desk. Pick the catchiest part of the song, choose a look, fine-tune, and post a lyric reel to social.</p>
              <ul>
                <li><Check />Pick the hook, pick a visual look</li>
                <li><Check />Lyric timing snapped to the beat</li>
                <li><Check />Render &amp; post to Instagram, TikTok, YouTube</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* REEL PREVIEW + REACH */}
      <section className="section tight" style={{ "--c": "var(--p-vision)" }}>
        <div className="wrap">
          <div className="card spotlight reveal" style={{ position: "relative", padding: "36px", overflow: "hidden" }}>
            <div style={{ position: "absolute", inset: 0, background: "radial-gradient(440px 320px at var(--mx,30%) var(--my,20%),rgba(168,119,255,.16),transparent 70%)", pointerEvents: "none" }} />
            <div style={{ position: "relative", display: "flex", gap: "30px", alignItems: "center", flexWrap: "wrap" }}>
              <div className="reel-phone">
                <ImageSlot id="v-reel" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} shape="rounded" radius={16} placeholder="Drop reel frame" />
                <div className="pl" style={{ zIndex: 2, pointerEvents: "none" }}><svg viewBox="0 0 24 24" width="14" height="14" fill="#fff"><path d="M8 5v14l11-7z" /></svg></div>
                <div className="cap" style={{ zIndex: 2, pointerEvents: "none" }}>Counting neon hours</div>
              </div>
              <div style={{ flex: 1, minWidth: "240px" }}>
                <span className="eyebrow" style={{ marginBottom: "14px" }}>Reach · after you post</span>
                <h3 style={{ fontSize: "28px", letterSpacing: "-0.03em", marginBottom: "10px", color: "var(--heading)" }}>Post it, then watch it move.</h3>
                <p style={{ fontSize: "15px", color: "var(--pebble)", lineHeight: 1.55, marginBottom: "22px", maxWidth: "42ch" }}>A post-ready lyric reel out the door, with reach and performance tracked once it's live.</p>
                <div className="statgrid" style={{ maxWidth: "440px" }}>
                  <div className="s"><div className="k">Output</div><div className="v">9:16</div></div>
                  <div className="s"><div className="k">Reach</div><div className="v"><span data-count="84.2" data-dec="1">0</span>K</div></div>
                  <div className="s"><div className="k">Saves</div><div className="v"><span data-count="3.1" data-dec="1">0</span>K</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <section className="section" style={{ "--c": "var(--p-vision)" }}>
        <div className="wrap">
          <div className="sec-head reveal"><span className="eyebrow">Connected to the rest of BNE</span><h2>Vision pulls from the whole rollout.</h2></div>
          <div className="eco-grid">
            <Link className="eco reveal" data-d="1" style={{ "--c": "var(--p-sync)" }} to="/sync"><div className="ei"><svg viewBox="0 0 24 24" fill="none"><path d="M4 6a2 2 0 012-2h4l2 2.5h6a2 2 0 012 2V18a2 2 0 01-2 2H6a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></div><b>Sync</b><p>Pull approved tracks and assets in.</p></Link>
            <Link className="eco reveal" data-d="2" style={{ "--c": "var(--p-dist)" }} to="/distribute"><div className="ei"><svg viewBox="0 0 24 24" fill="none"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></div><b>Distribution</b><p>Build visuals around a live release.</p></Link>
            <Link className="eco reveal" data-d="3" style={{ "--c": "var(--p-live)" }} to="/live"><div className="ei"><svg viewBox="0 0 24 24" fill="none"><path d="M4 8a2 2 0 012-2h12a2 2 0 012 2 2 2 0 000 4 2 2 0 000 4 2 2 0 01-2 2H6a2 2 0 01-2-2 2 2 0 000-4 2 2 0 000-4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></div><b>BNE Live</b><p>Make a reel to promote the date.</p></Link>
            <Link className="eco reveal" data-d="4" style={{ "--c": "var(--p-contracts)" }} to="/contracts"><div className="ei"><svg viewBox="0 0 24 24" fill="none"><path d="M14 3v4a1 1 0 0 0 1 1h4M18 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8l6 6v3M8 18c1.4-2 2.8-2 4 0s2.6 2 4 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></div><b>Contracts</b><p>Clear terms for directors &amp; crew.</p></Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section tight">
        <div className="wrap">
          <div className="cta-band reveal-scale">
            <div className="glow" />
            <span className="eyebrow center" style={{ justifyContent: "center", marginBottom: "20px" }}>Create · Vision</span>
            <h2 className="grad-text">Give the song a face.</h2>
            <p>Upload a track and plan the video — or ship a reel before the day's out.</p>
            <div className="row" style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
              <a className="btn btn-primary btn-lg btn-arrow" href={VISION_URL}>Create a Visual <Arrow /></a>
              <Link className="btn btn-ghost btn-lg" to="/home">Explore the Ecosystem</Link>
            </div>
          </div>
        </div>
      </section>
    </NightLayout>
  );
};

export default VisionPage;
